import { t } from '@hefe/i18n';
import { BasketballGame, BasketballTeamGame, SelectOption } from '@hefe/models';
import {
  BasketballTeamSlice,
  usePrevious,
  userActiveBasketballCoachTeamId,
} from '@hefe/store';
import { formatDateTime } from '@hefe/utils';
import { Select } from 'antd';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';

export default function TeamJerseysPage() {
  const dispatch = useAppDispatch();

  const activeTeamId = useSelector(userActiveBasketballCoachTeamId);
  const prevActiveCoachId = usePrevious(activeTeamId);
  const games = useSelector(BasketballTeamSlice.basketballTeamGamesSelector);
  const playersSelect = useSelector(
    BasketballTeamSlice.basketballTeamPlayersSelectWithPendingSelector
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    fetchData();
    fetchTeamPlayers();
  }, []);

  useEffect(() => {
    if (prevActiveCoachId !== undefined && activeTeamId !== prevActiveCoachId) {
      fetchData();
      fetchTeamPlayers();
    }
  }, [activeTeamId]);

  const fetchData = () => {
    setLoading(true);
    dispatch(BasketballTeamSlice.fetchBasketballTeamGames())
      .unwrap()
      .finally(() => setLoading(false));
  };

  const fetchTeamPlayers = () => {
    if (!activeTeamId) {
      return;
    }

    dispatch(
      BasketballTeamSlice.fetchBasketballTeamPlayersSelect({
        teamId: activeTeamId,
        cacheDuration: 5 * 60 * 1000,
      })
    );
  };

  const setSelectFieldSaving = (save: boolean, gameId: number) => {
    setSaving((state) =>
      produce(state, (draft) => {
        if (save) {
          draft[gameId] = true;
        } else {
          draft[gameId] = false;
        }
      })
    );
  };

  const saveAssignment = (value: number | undefined, gameId: number) => {
    setSelectFieldSaving(true, gameId);
    dispatch(
      BasketballTeamSlice.updateBasketballTeamJerseys({
        id: gameId,
        jerseys: value,
      })
    )
      .unwrap()
      .finally(() => setSelectFieldSaving(false, gameId));
  };

  const renderPlayerSelect = (
    game: BasketballGame,
    teamGame: BasketballTeamGame
  ) => {
    const isSaving = !!saving[game.id];
    return (
      <Select
        style={{ width: '100%' }}
        options={playersSelect}
        value={teamGame?.jerseys ? teamGame?.jerseys?.id : null}
        onChange={(value) => saveAssignment(value, game.id)}
        loading={isSaving}
      />
    );
  };

  return (
    <CustomTable
      name="team-judges"
      rowKey={'id'}
      loading={loading}
      dataSource={games}
      columns={[
        {
          key: 'date',
          title: t('date'),
          dataIndex: ['game', 'date'],
          default: true,
          render: (value) => formatDateTime(value),
          showDateFilter: true,
        },
        {
          key: 'game',
          title: t('games.singular'),
          default: true,
          render: (value) => `${value?.game?.home} - ${value?.game?.guest}`,
          showFilter: true,
        },
        {
          key: 'jerseys',
          title: t('jerseys'),
          dataIndex: 'jerseys',
          default: true,
          render: (value: SelectOption, item: any) => {
            const game = item?.game as BasketballGame;
            const teamGame = item as BasketballTeamGame;
            return renderPlayerSelect(game, teamGame);
          },
        },
      ]}
    />
  );
}
